window.FenluxLayoutEditor = (function () {
  function create(container, options) {
    const state = {
      nodes: [],
      onChange: options.onChange || function () {},
      addable: options.addable || [],
      minScalePxPerMm: Number(options.minScalePxPerMm) || 0.035,
      maxScalePxPerMm: Number(options.maxScalePxPerMm) || 0.18,
      preferredScalePxPerMm: Number(options.scalePxPerMm) || 0.08,
      gapVisualScale: Number(options.gapVisualScale) || 1,
      minVisibleGapPx: Number(options.minVisibleGapPx) || 0,
      selectedUid: null,
      isCoarsePointer: window.matchMedia ? window.matchMedia('(pointer: coarse)').matches : false
    };

    function safeNum(value, fallback) {
      const n = Number(value);
      return Number.isFinite(n) ? n : (fallback || 0);
    }

    function roleClass(role) {
      if (role === 'gate') return 'fenlux-layout-node--gate';
      if (role === 'panel') return 'fenlux-layout-node--panel';
      if (role === 'post') return 'fenlux-layout-node--post';
      return '';
    }

    function mmToPx(mm, scale, minPx) {
      const px = Math.round(safeNum(mm, 0) * scale);
      return Math.max(minPx || 0, px);
    }

    function gapMmToPx(mm, scale) {
      const gapMm = safeNum(mm, 0);
      if (gapMm <= 0) return 0;

      const visualScale = Math.max(0, state.gapVisualScale);
      const raw = Math.round(gapMm * scale * visualScale);

      return Math.max(state.minVisibleGapPx || 0, raw);
    }

    function notify() {
      state.onChange(state.nodes.slice());
    }

    function getNodeBodyWidthMm(node) {
      if (safeNum(node.previewBodyWidthMm, 0) > 0) return safeNum(node.previewBodyWidthMm, 0);
      if (safeNum(node.bodyWidthMm, 0) > 0) return safeNum(node.bodyWidthMm, 0);
      if (safeNum(node.widthMm, 0) > 0) return safeNum(node.widthMm, 0);
      return 0;
    }

    function getNodeHeightMm(node) {
      if (safeNum(node.previewHeightMm, 0) > 0) return safeNum(node.previewHeightMm, 0);
      if (safeNum(node.heightMm, 0) > 0) return safeNum(node.heightMm, 0);
      return 1800;
    }

    function getNodeLeftGapMm(node) {
      return Math.max(0, safeNum(node.leftGapMm, 0));
    }

    function getNodeRightGapMm(node) {
      return Math.max(0, safeNum(node.rightGapMm, 0));
    }

    function getNodeTotalWidthMm(node) {
      return getNodeLeftGapMm(node) + getNodeBodyWidthMm(node) + getNodeRightGapMm(node);
    }

    function calcUsedWidthMm(nodes) {
      return (nodes || []).reduce(function (sum, node) {
        return sum + getNodeTotalWidthMm(node);
      }, 0);
    }

    function calcMaxHeightMm(nodes) {
      const max = (nodes || []).reduce(function (m, node) {
        return Math.max(m, getNodeHeightMm(node));
      }, 0);
      return max || 1800;
    }

    function calcRenderedWidthPx(nodes, scale) {
      return (nodes || []).reduce(function (sum, node) {
        const bodyWidthPx = mmToPx(getNodeBodyWidthMm(node), scale, 1);
        const leftGapPx = gapMmToPx(getNodeLeftGapMm(node), scale);
        const rightGapPx = gapMmToPx(getNodeRightGapMm(node), scale);
        return sum + bodyWidthPx + leftGapPx + rightGapPx;
      }, 0);
    }

    function renderMeta(usedWidth, openingWidth) {
      const meta = document.createElement('div');
      meta.className = 'fenlux-layout-meta';

      const cutRequired = usedWidth > openingWidth;
      meta.innerHTML = `
        <div><strong>Opening width:</strong> ${Math.round(openingWidth)} mm</div>
        <div><strong>Layout used width:</strong> ${Math.round(usedWidth)} mm</div>
        <div class="${cutRequired ? 'fenlux-bundle-warning' : ''}">
          <strong>${cutRequired ? 'On-site cut required' : 'Remaining'}:</strong>
          ${Math.round(Math.abs(openingWidth - usedWidth))} mm
        </div>
      `;
      return meta;
    }

    function buildToolbar() {
      const toolbar = document.createElement('div');
      toolbar.className = 'fenlux-layout-toolbar';

      (state.addable || []).forEach(function (item) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'fenlux-bundle-btn fenlux-bundle-btn--secondary';
        btn.textContent = `Add ${item.label}`;
        btn.addEventListener('click', function () {
          state.nodes.push({
            uid: `${item.role}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            role: item.role,
            label: item.label,
            widthMm: safeNum(item.widthMm, 0),
            bodyWidthMm: safeNum(item.bodyWidthMm, safeNum(item.widthMm, 0)),
            heightMm: safeNum(item.heightMm, 1800),
            previewHeightMm: safeNum(item.previewHeightMm, safeNum(item.heightMm, 1800)),
            leftGapMm: safeNum(item.leftGapMm, 0),
            rightGapMm: safeNum(item.rightGapMm, 0),
            image: item.image || '',
            componentKey: item.componentKey || ''
          });
          state.selectedUid = state.nodes[state.nodes.length - 1].uid;
          render();
          notify();
        });
        toolbar.appendChild(btn);
      });

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'fenlux-bundle-btn fenlux-bundle-btn--secondary';
      removeBtn.textContent = 'Remove Last';
      removeBtn.addEventListener('click', function () {
        if (!state.nodes.length) return;
        const removed = state.nodes.pop();
        if (removed && removed.uid === state.selectedUid) {
          state.selectedUid = state.nodes.length ? state.nodes[state.nodes.length - 1].uid : null;
        }
        render();
        notify();
      });
      toolbar.appendChild(removeBtn);

      return toolbar;
    }

    function getAvailableTrackSize() {
      const containerWidth = container.clientWidth || 900;
      const availableWidthPx = Math.max(240, containerWidth - 8);

      let availableHeightPx = 320;
      const previewCard = container.closest('.fenlux-bundle-card--preview');
      if (previewCard) {
        const cardStyles = window.getComputedStyle(previewCard);
        const padTop = parseFloat(cardStyles.paddingTop) || 0;
        const padBottom = parseFloat(cardStyles.paddingBottom) || 0;
        const cardHeight = previewCard.clientHeight || 0;
        if (cardHeight > 0) {
          availableHeightPx = Math.max(220, Math.min(420, cardHeight - padTop - padBottom - 180));
        }
      }

      return {
        width: availableWidthPx,
        height: availableHeightPx
      };
    }

    function getAdaptiveScale(nodes) {
      const totalWidthMm = Math.max(1, calcUsedWidthMm(nodes));
      const maxHeightMm = Math.max(1, calcMaxHeightMm(nodes));
      const area = getAvailableTrackSize();

      const widthFitScale = area.width / totalWidthMm;
      const heightFitScale = area.height / maxHeightMm;

      let scale = Math.min(widthFitScale, heightFitScale);

      if (!Number.isFinite(scale) || scale <= 0) {
        scale = state.preferredScalePxPerMm;
      }

      scale = Math.min(scale, state.maxScalePxPerMm);
      scale = Math.max(scale, state.minScalePxPerMm);

      return scale;
    }

    function createGapEl(type, widthPx, heightPx, mmValue) {
      const el = document.createElement('div');
      el.className = `fenlux-layout-node__${type}-gap`;
      el.style.width = `${widthPx}px`;
      el.style.height = `${heightPx}px`;
      el.style.flex = `0 0 ${widthPx}px`;
      el.dataset.mm = String(mmValue || 0);
      return el;
    }

    function createBodyEl(node, bodyWidthPx, heightPx) {
      const body = document.createElement('div');
      body.className = 'fenlux-layout-node__body';
      body.style.width = `${bodyWidthPx}px`;
      body.style.height = `${heightPx}px`;
      body.style.flex = `0 0 ${bodyWidthPx}px`;

      if (node.image) {
        const img = document.createElement('img');
        img.className = 'fenlux-layout-node__img';
        img.src = node.image;
        img.alt = node.label || '';
        img.draggable = false;
        body.appendChild(img);
      }

      return body;
    }

    function findNodeIndex(uid) {
      return state.nodes.findIndex(function (node) {
        return node.uid === uid;
      });
    }

    function moveSelected(offset) {
      const index = findNodeIndex(state.selectedUid);
      if (index === -1) return;

      const nextIndex = index + offset;
      if (nextIndex < 0 || nextIndex >= state.nodes.length) return;

      const moved = state.nodes.splice(index, 1)[0];
      state.nodes.splice(nextIndex, 0, moved);
      render();
      notify();
    }

    function removeSelected() {
      const index = findNodeIndex(state.selectedUid);
      if (index === -1) return;

      state.nodes.splice(index, 1);

      if (!state.nodes.length) {
        state.selectedUid = null;
      } else {
        const nextIndex = Math.max(0, index - 1);
        state.selectedUid = state.nodes[nextIndex].uid;
      }

      render();
      notify();
    }

    function createNodeEl(node, scale) {
      const bodyWidthMm = getNodeBodyWidthMm(node);
      const heightMm = getNodeHeightMm(node);
      const leftGapMm = getNodeLeftGapMm(node);
      const rightGapMm = getNodeRightGapMm(node);

      const bodyWidthPx = mmToPx(bodyWidthMm, scale, 1);
      const heightPx = mmToPx(heightMm, scale, 60);
      const leftGapPx = gapMmToPx(leftGapMm, scale);
      const rightGapPx = gapMmToPx(rightGapMm, scale);
      const totalWidthPx = leftGapPx + bodyWidthPx + rightGapPx;

      const el = document.createElement('div');
      el.className = `fenlux-layout-node ${roleClass(node.role)}`;
      if (node.uid === state.selectedUid) {
        el.classList.add('is-selected');
      }

      el.draggable = !state.isCoarsePointer;
      el.dataset.uid = node.uid;
      el.style.width = `${totalWidthPx}px`;
      el.style.height = `${heightPx}px`;
      el.style.flex = `0 0 ${totalWidthPx}px`;

      el.addEventListener('click', function () {
        state.selectedUid = node.uid;
        render();
      });

      if (leftGapPx > 0) {
        el.appendChild(createGapEl('left', leftGapPx, heightPx, leftGapMm));
      }

      el.appendChild(createBodyEl(node, bodyWidthPx, heightPx));

      if (rightGapPx > 0) {
        el.appendChild(createGapEl('right', rightGapPx, heightPx, rightGapMm));
      }

      return el;
    }

    function reorderByDrop(fromUid, targetUid, placement) {
      const fromIndex = findNodeIndex(fromUid);
      const toIndex = findNodeIndex(targetUid);

      if (fromIndex === -1 || toIndex === -1 || fromUid === targetUid) return;

      const moved = state.nodes.splice(fromIndex, 1)[0];
      let insertIndex = toIndex;

      if (placement === 'after') {
        insertIndex = fromIndex < toIndex ? toIndex : toIndex + 1;
      } else {
        insertIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
      }

      if (insertIndex < 0) insertIndex = 0;
      if (insertIndex > state.nodes.length) insertIndex = state.nodes.length;

      state.nodes.splice(insertIndex, 0, moved);
      state.selectedUid = moved.uid;
    }

    function buildSelectionBar() {
      const selectedIndex = findNodeIndex(state.selectedUid);
      if (selectedIndex === -1) return null;

      const selectedNode = state.nodes[selectedIndex];

      const bar = document.createElement('div');
      bar.className = 'fenlux-layout-selectionbar';

      const label = document.createElement('div');
      label.className = 'fenlux-layout-selectionbar__label';
      label.textContent = `Selected: ${selectedNode.label || selectedNode.role}`;
      bar.appendChild(label);

      const actions = document.createElement('div');
      actions.className = 'fenlux-layout-selectionbar__actions';

      const leftBtn = document.createElement('button');
      leftBtn.type = 'button';
      leftBtn.className = 'fenlux-bundle-btn fenlux-bundle-btn--secondary fenlux-layout-selectionbar__btn';
      leftBtn.textContent = 'Move Left';
      leftBtn.disabled = selectedIndex === 0;
      leftBtn.addEventListener('click', function () {
        moveSelected(-1);
      });

      const rightBtn = document.createElement('button');
      rightBtn.type = 'button';
      rightBtn.className = 'fenlux-bundle-btn fenlux-bundle-btn--secondary fenlux-layout-selectionbar__btn';
      rightBtn.textContent = 'Move Right';
      rightBtn.disabled = selectedIndex === state.nodes.length - 1;
      rightBtn.addEventListener('click', function () {
        moveSelected(1);
      });

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'fenlux-bundle-btn fenlux-bundle-btn--secondary fenlux-layout-selectionbar__btn';
      removeBtn.textContent = 'Remove';
      removeBtn.addEventListener('click', function () {
        removeSelected();
      });

      actions.appendChild(leftBtn);
      actions.appendChild(rightBtn);
      actions.appendChild(removeBtn);
      bar.appendChild(actions);

      return bar;
    }

    function applyGreenIndicatorStyles(marker) {
      marker.style.background = '#22c55e';
      marker.style.boxShadow = '0 0 0 2px rgba(34, 197, 94, 0.28), 0 0 12px rgba(34, 197, 94, 0.35)';
      marker.style.border = '0';
      marker.style.opacity = '1';
      marker.style.zIndex = '999';
    }

    function attachDesktopDrag(track) {
      const marker = document.createElement('div');
      marker.className = 'fenlux-layout-drop-indicator';
      marker.hidden = true;
      applyGreenIndicatorStyles(marker);
      track.appendChild(marker);

      let dragUid = null;
      let markerTargetUid = null;
      let markerPlacement = 'before';

      function clearMarker() {
        marker.hidden = true;
        markerTargetUid = null;
      }

      function placeMarker(targetEl, placement) {
        const body = targetEl.querySelector('.fenlux-layout-node__body') || targetEl;
        const trackRect = track.getBoundingClientRect();
        const rect = body.getBoundingClientRect();
        const left = placement === 'before'
          ? rect.left - trackRect.left
          : rect.right - trackRect.left;

        marker.style.left = `${Math.round(left - 2)}px`;
        marker.hidden = false;
        applyGreenIndicatorStyles(marker);
      }

      track.querySelectorAll('.fenlux-layout-node').forEach(function (el) {
        el.addEventListener('dragstart', function () {
          dragUid = el.dataset.uid;
          el.classList.add('is-dragging');
          state.selectedUid = dragUid;
        });

        el.addEventListener('dragend', function () {
          dragUid = null;
          markerTargetUid = null;
          el.classList.remove('is-dragging');
          clearMarker();
        });

        el.addEventListener('dragover', function (e) {
          e.preventDefault();
          if (!dragUid) return;

          const rect = el.getBoundingClientRect();
          const placement = (e.clientX - rect.left) < rect.width / 2 ? 'before' : 'after';
          markerTargetUid = el.dataset.uid;
          markerPlacement = placement;
          placeMarker(el, placement);
        });

        el.addEventListener('drop', function (e) {
          e.preventDefault();
          if (!dragUid || !markerTargetUid) return;

          reorderByDrop(dragUid, markerTargetUid, markerPlacement);
          clearMarker();
          dragUid = null;
          render();
          notify();
        });
      });

      track.addEventListener('dragleave', function (e) {
        if (!track.contains(e.relatedTarget)) {
          clearMarker();
        }
      });
    }

    function renderTrack(scale) {
      const wrap = document.createElement('div');
      wrap.className = 'fenlux-layout-track-wrap';

      const track = document.createElement('div');
      track.className = 'fenlux-layout-track';

      const renderedWidthPx = calcRenderedWidthPx(state.nodes, scale);
      const maxHeightPx = mmToPx(calcMaxHeightMm(state.nodes), scale, 120);

      track.style.width = `${renderedWidthPx}px`;
      track.style.minHeight = `${maxHeightPx}px`;

      state.nodes.forEach(function (node) {
        const el = createNodeEl(node, scale);
        track.appendChild(el);
      });

      if (!state.isCoarsePointer) {
        attachDesktopDrag(track);
      }

      wrap.appendChild(track);
      return wrap;
    }

    function render() {
      container.innerHTML = '';

      const toolbar = buildToolbar();
      container.appendChild(toolbar);

      const scale = getAdaptiveScale(state.nodes);
      const trackWrap = renderTrack(scale);
      container.appendChild(trackWrap);

      const selectionBar = buildSelectionBar();
      if (selectionBar) {
        container.appendChild(selectionBar);
      }

      const usedWidth = calcUsedWidthMm(state.nodes);
      container.appendChild(renderMeta(usedWidth, Number(options.openingWidth) || 0));
    }

    return {
      setNodes: function (nodes) {
        state.nodes = Array.isArray(nodes) ? nodes.map(function (node, index) {
          const copy = Object.assign({}, node);
          if (!copy.uid) {
            copy.uid = `${copy.role || 'node'}-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`;
          }

          copy.widthMm = safeNum(copy.widthMm, 0);
          copy.bodyWidthMm = safeNum(copy.bodyWidthMm, copy.widthMm);
          copy.heightMm = safeNum(copy.heightMm, 1800);
          copy.previewHeightMm = safeNum(copy.previewHeightMm, copy.heightMm);
          copy.leftGapMm = safeNum(copy.leftGapMm, 0);
          copy.rightGapMm = safeNum(copy.rightGapMm, 0);

          return copy;
        }) : [];

        if (state.selectedUid && findNodeIndex(state.selectedUid) === -1) {
          state.selectedUid = state.nodes.length ? state.nodes[0].uid : null;
        }

        if (!state.selectedUid && state.nodes.length) {
          state.selectedUid = state.nodes[0].uid;
        }

        render();
      },

      setOpeningWidth: function (width) {
        options.openingWidth = width;
        render();
      },

      updateAddable: function (addable) {
        state.addable = Array.isArray(addable) ? addable.slice() : [];
        render();
      },

      updatePreviewGapSettings: function (gapVisualScale, minVisibleGapPx) {
        state.gapVisualScale = Number(gapVisualScale) || 1;
        state.minVisibleGapPx = Number(minVisibleGapPx) || 0;
        render();
      },

      getNodes: function () {
        return state.nodes.slice();
      }
    };
  }

  return { create: create };
})();